// react-redux imports
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useBottomScrollListener } from "react-bottom-scroll-listener";
import { Waypoint } from "react-waypoint";
import { CSSTransition } from "react-transition-group";
import { RootState } from "app/store";
import { setFirstTimeEntering } from "app/appSlice";
import {
  setHeaderAction,
  setHeaderColorMode,
  setHeaderMenuColor,
  setHeaderWidthMode,
} from "features/header/headerSlice";
import { setUrl } from "features/background/backgroundSlice";
import { setIsAuthDialogOpen } from "features/authDialog/authDialogSlice";
import AuthDialog from "features/authDialog/AuthDialog";

// common imports
import FlexColumn from "common/components/styled-components/FlexColumn";
import ScrollIndicator from "common/components/ScrollIndicator";
import StandardButton from "common/components/styled-components/StandardButton";
import homeBackground from "common/img/home-background.jpg";
import introVideo from "common/video/intro.mp4";
import { getTouchDirection, getWheelDirection } from "common/utils/scroll";
import { getInitialSteps, getProgressStep } from "common/utils/stepper";

import Definition from "./Definition";
import { Sections, SECTIONS } from "./constants/sections";

// styled-components imports
import ActionButtonWrapper from "./styled-components/ActionButtonWrapper";
import Container from "./styled-components/Container";
import ScrollContainer from "./styled-components/ScrollContainer";
import Stepper, { StepperProps } from "common/components/Stepper";
import Title from "./styled-components/Title";
import Video from "./styled-components/Video";
import StepperContainer from "common/components/styled-components/StepperContainer";
import { trackCalculationBegins } from "features/cookie-banner/gtm-functions";

function Home() {
  const dispatch = useDispatch();
  const history = useHistory();
  const [hitTheBottom, setHitTheBottom] = useState<boolean>(false);
  const [wheelEvents, setWheelEvents] = useState<React.WheelEvent[]>([]);
  const [touchEvents, setTouchEvents] = useState<React.TouchEvent[]>([]);
  const [timeoutID, setTimeoutID] = useState<number>();
  const [step, setStep] = useState<number>(1);
  const [stepperData, setStepperData] = useState<StepperProps>({
    steps: getInitialSteps<Sections>(SECTIONS),
    currentStep: 0,
  });
  const isAuthDialogOpen = useSelector(
    (state: RootState) => state.authDialog.isAuthDialogOpen
  );
  const upDesktopScreen = useSelector(
    (state: RootState) => state.app.upDesktopScreen
  );
  const firstTimeEntering = useSelector(
    (state: RootState) => state.app.firstTimeEntering
  );
  const auth = useSelector((state: RootState) => state.auth);
  const isUserAuthenticated = !Object.values(auth).includes(undefined);

  const scrollRef = useBottomScrollListener<HTMLDivElement>(
    () => {
      setHitTheBottom(true);
    },
    { debounce: 0, debounceOptions: { leading: false } }
  );

  function handleCalculateButton(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    trackCalculationBegins();
    history.push("/calculator");
  }

  function handleSignInButton(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    dispatch(setIsAuthDialogOpen(true));
  }

  function handleAuthClose() {
    dispatch(setIsAuthDialogOpen(false));
  }

  function handleAuthSuccess() {
    history.push("calculator/result", { doNotUpdate: true });
    dispatch(setIsAuthDialogOpen(false));
  }

  function handleVideoOnEnded() {
    dispatch(setFirstTimeEntering(false));
  }

  function handleScroll(e: React.WheelEvent | React.TouchEvent) {
    window.clearTimeout(timeoutID);
    const { type } = e;
    if (type === "wheel") {
      const events = Array.from(wheelEvents);
      events.push(e as React.WheelEvent);
      setWheelEvents(events);

      const timeout = window.setTimeout(() => {
        if (hitTheBottom) {
          if (getWheelDirection(events) > 0) history.push("/factors");
        }
        setWheelEvents([]);
      }, 150);
      setTimeoutID(timeout);
    } else if (type === "touchmove") {
      const events = Array.from(touchEvents);
      events.push(e as React.TouchEvent);
      setTouchEvents(events);

      const timeout = window.setTimeout(() => {
        if (hitTheBottom) {
          if (getTouchDirection(events) > 0) history.push("/factors");
        }
        setTouchEvents([]);
      }, 150);
      setTimeoutID(timeout);
    }
  }

  function handleWaypoint(sectionStep: number) {
    const { intro: introSection } = SECTIONS;
    return (options: Waypoint.CallbackArgs) => {
      switch (sectionStep) {
        case 1: {
          const { currentPosition, previousPosition } = options;
          if (currentPosition === "inside" && previousPosition === "above") {
            setStepperData(
              getProgressStep(stepperData, introSection, step - 1)
            );
            setStep(step - 1);
          }
          break;
        }

        case 2: {
          const { currentPosition, previousPosition } = options;
          if (currentPosition === "inside" && previousPosition === "below") {
            setStepperData(
              getProgressStep(stepperData, introSection, step + 1)
            );
            setStep(step + 1);
          }
          break;
        }
      }
    };
  }

  useEffect(() => {
    dispatch(setHeaderWidthMode("full"));
    dispatch(setHeaderAction("menu"));
    dispatch(setHeaderColorMode("unicolor"));
    dispatch(setHeaderMenuColor(upDesktopScreen ? "#ffffff" : "#363837"));
    dispatch(setUrl(homeBackground));

    return () => {
      dispatch(setIsAuthDialogOpen(false));
      window.clearTimeout(timeoutID);
    };
  }, []);

  return (
    <>
      {/* <CSSTransition
        classNames="video"
        timeout={500}
        in={firstTimeEntering}
        unmountOnExit
        appear
      >
        <>
          <div style={{ zIndex: 200, position: 'absolute', display: 'flex', justifyContent: 'flex-end', width: '98vw' }} >
            <a>
              <h1 style={{ color: 'white' }} onClick={handleVideoOnEnded}>X</h1>
            </a>
          </div>
          <Video onEnded={handleVideoOnEnded} muted autoPlay>
            <source src={introVideo} type="video/mp4" />
          </Video>
        </>
      </CSSTransition> */}
      {upDesktopScreen && (
        <StepperContainer left={20}>
          <Stepper {...stepperData} />
        </StepperContainer>
      )}
      <ScrollContainer
        onWheel={handleScroll}
        onTouchMove={handleScroll}
        ref={scrollRef}
      >
        <Waypoint onPositionChange={handleWaypoint(1)} topOffset={156}>
          <Container>
            {isAuthDialogOpen && (
              <AuthDialog
                handleClose={handleAuthClose}
                successCallback={handleAuthSuccess}
                sendCalculatorData={false}
                isSignInOnly
              />
            )}
            <Title>Tu huella de carbono</Title>
            <FlexColumn gap={upDesktopScreen ? 20 : 10}>
              <ActionButtonWrapper>
                <StandardButton onClick={handleCalculateButton}>
                  Calcular
                </StandardButton>
              </ActionButtonWrapper>
              {!isUserAuthenticated && (
                <ActionButtonWrapper>
                  <StandardButton onClick={handleSignInButton} secondary>
                    Iniciar sesión
                  </StandardButton>
                </ActionButtonWrapper>
              )}
            </FlexColumn>
            <ScrollIndicator description="¿Qué es la huella de carbono?" />
          </Container>
        </Waypoint>
        <Definition handleWaypoint={handleWaypoint} />
      </ScrollContainer>
    </>
  );
}

export default Home;
